import React from 'react';

import { InputAdornment, MenuItem, TableCell, TableRow, TextField, Typography } from '@mui/material';

import { audMoney } from '../constants';
import {
  type DealOptionGroup,
  PACKAGE_ROLES,
  type PackageRole,
  type PackageRoleChoice,
} from '../tabs/packageOptions/previousPackageVariants';

import { PACKAGE_ROLE_LABELS } from './copyFromPreviousModel';

export type PackageRoleChange = (key: string, choice: PackageRoleChoice) => void;

// Until the tool can attach an experience, a bundled package is skipped or copied as a plain Upgrade.
const BUNDLED_ROLES: Array<PackageRole> = ['skip', 'upgrade'];

type CopyPackageVariantsRowProps = {
  choice: PackageRoleChoice;
  group: DealOptionGroup;
  missingSuffix: boolean;
  onChange: PackageRoleChange;
};

function staysOf({ dealsByLos }: DealOptionGroup): string {
  return Object.entries(dealsByLos)
    .map(([nights, deals]) => `${nights}N ${audMoney(Math.min(...deals.map((deal) => Number(deal.price__c))))}`)
    .join(' · ');
}

export default function CopyPackageVariantsRow({
  choice,
  group,
  missingSuffix,
  onChange,
}: CopyPackageVariantsRowProps) {
  const roles = group.bundled ? BUNDLED_ROLES : PACKAGE_ROLES;

  function handleRoleChange(event: React.ChangeEvent<HTMLInputElement>) {
    const role = PACKAGE_ROLES.find((packageRole) => packageRole === event.target.value);
    if (role) {
      onChange(group.key, { ...choice, role });
    }
  }

  function handleSuffixChange(event: React.ChangeEvent<HTMLInputElement>) {
    onChange(group.key, { ...choice, suffix: event.target.value });
  }

  function renderPackageOptionsName() {
    if (choice.role === 'skip') {
      return (
        <Typography color="text.secondary" variant="body2">
          Not copied
        </Typography>
      );
    }
    if (choice.role === 'base') {
      return <Typography variant="body2">{group.asset.name}</Typography>;
    }
    return (
      <TextField
        InputProps={{ startAdornment: <InputAdornment position="start">{group.asset.name} -</InputAdornment> }}
        error={missingSuffix}
        fullWidth
        onChange={handleSuffixChange}
        placeholder="e.g. Spa upgrade"
        size="small"
        value={choice.suffix}
      />
    );
  }

  return (
    <TableRow>
      <TableCell>
        {group.name || 'No name'}
        {group.bundled && (
          <Typography color="text.secondary" display="block" variant="caption">
            Bundled experience, copied without it
          </Typography>
        )}
      </TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>{staysOf(group)}</TableCell>
      <TableCell sx={{ minWidth: 170 }}>
        <TextField fullWidth onChange={handleRoleChange} select size="small" value={choice.role}>
          {roles.map((role) => (
            <MenuItem key={role} value={role}>
              {PACKAGE_ROLE_LABELS[role]}
            </MenuItem>
          ))}
        </TextField>
      </TableCell>
      <TableCell sx={{ minWidth: 380 }}>{renderPackageOptionsName()}</TableCell>
    </TableRow>
  );
}
